import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../services/api';

interface UserOption {
  id: string;
  name: string;
  avatar_url?: string | null;
  email?: string | null;
}

interface SavedPerson {
  id: number;
  notion_user_id: string;
  name: string;
  avatar_url?: string | null;
}

interface Task {
  id: string;
  url?: string;
  title: string;
  status: string;
  priority: string;
  due?: string | null;
  start?: string | null;
  end?: string | null;
  assignees: string[];
  milestone?: string | null;
  team?: string | null;
}

interface Workload {
  total: number;
  tasks: Task[];
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
}

const STATUS_ORDER = [
  'Not started', 'Scoping', 'In progress', 'BN Review', 'Client Review',
  'Blocked', 'Icebox',
];

const PRIORITY_ORDER = ['P0', 'P1', 'P2', 'P3', 'P4', 'P5', 'High', 'Medium', 'No Priority'];

const STATUS_COLORS: Record<string, string> = {
  'Not started': 'bg-gray-100 text-gray-800 border-gray-200',
  'In progress': 'bg-blue-100 text-blue-800 border-blue-200',
  'BN Review': 'bg-pink-100 text-pink-800 border-pink-200',
  'Client Review': 'bg-orange-100 text-orange-800 border-orange-200',
  'Icebox': 'bg-cyan-100 text-cyan-800 border-cyan-200',
  'Scoping': 'bg-purple-100 text-purple-800 border-purple-200',
  'Blocked': 'bg-red-100 text-red-800 border-red-200',
};

const PRIORITY_COLORS: Record<string, string> = {
  P0: 'bg-red-100 text-red-800 border-red-200',
  P1: 'bg-orange-100 text-orange-800 border-orange-200',
  P2: 'bg-yellow-100 text-yellow-800 border-yellow-200',
  P3: 'bg-blue-100 text-blue-800 border-blue-200',
  P4: 'bg-green-100 text-green-800 border-green-200',
  P5: 'bg-gray-100 text-gray-700 border-gray-200',
  High: 'bg-pink-100 text-pink-800 border-pink-200',
  Medium: 'bg-violet-100 text-violet-800 border-violet-200',
  'No Priority': 'bg-gray-50 text-gray-600 border-gray-200',
};

// Locale is pinned rather than left to the browser: on a Spanish-locale
// browser `undefined` renders August as "26 ago", which reads as "26 ago"
// (as in "ago") next to the rest of this English UI. Notion dates are plain
// date strings, so parse as UTC to avoid a timezone shifting the day.
const DATE_LOCALE = 'en-US';

const formatDue = (iso?: string | null) => {
  if (!iso) return '—';
  const d = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(d.getTime())) return iso;
  const sameYear = d.getUTCFullYear() === new Date().getUTCFullYear();
  return d.toLocaleDateString(DATE_LOCALE, {
    month: 'short', day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
    timeZone: 'UTC',
  });
};

const sameYMD = (iso: string, y: number, m: number, d: number) => {
  // A Notion date-only value ("2026-08-27") parses as UTC midnight, so reading
  // it back with local getters lands on the previous day in any negative-offset
  // timezone. Compare the date parts as UTC instead.
  const dt = new Date(iso.length === 10 ? `${iso}T00:00:00Z` : iso);
  if (Number.isNaN(dt.getTime())) return false;
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m && dt.getUTCDate() === d;
};

const monthLabel = (y: number, m: number) =>
  new Date(y, m, 1).toLocaleDateString(DATE_LOCALE, { month: 'long', year: 'numeric' });

const DAY_HEADERS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const PeopleWorkloadPanel: React.FC = () => {
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [saved, setSaved] = useState<SavedPerson[]>([]);
  const [query, setQuery] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const searchRef = useRef<HTMLDivElement | null>(null);

  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string>('');
  const [workload, setWorkload] = useState<Workload | null>(null);
  const [loadingWorkload, setLoadingWorkload] = useState(false);
  const [refreshedAt, setRefreshedAt] = useState<string | null>(null);
  const [error, setError] = useState<string>('');

  // Click-to-list popover (status/priority pills)
  const [popover, setPopover] = useState<{ kind: 'status' | 'priority'; key: string } | null>(null);

  // Calendar navigation state
  const today = new Date();
  const [calYear, setCalYear] = useState<number>(today.getFullYear());
  const [calMonth, setCalMonth] = useState<number>(today.getMonth());

  // ----- Initial loads -----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoadingUsers(true);
        const [u, s] = await Promise.all([
          api.get('/people-workload/users'),
          api.get('/people-workload/saved'),
        ]);
        if (cancelled) return;
        setUsers(u.data?.users || []);
        setSaved(s.data?.saved || []);
      } catch (err: any) {
        if (!cancelled) setError(err.response?.data?.detail || err.message || 'Failed to load');
      } finally {
        if (!cancelled) setLoadingUsers(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ----- Click-outside to close dropdown -----
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q)
    );
  }, [users, query]);

  const isSaved = (notion_user_id: string) =>
    saved.some((s) => s.notion_user_id === notion_user_id);

  // ----- Handlers -----
  const loadWorkload = async (userId: string, displayName: string) => {
    setSelectedUserId(userId);
    setSelectedName(displayName);
    setWorkload(null);
    setError('');
    setLoadingWorkload(true);
    try {
      const resp = await api.get(`/people-workload/workload/${userId}`);
      setWorkload(resp.data);
      setRefreshedAt(new Date().toLocaleString(DATE_LOCALE));
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to fetch workload');
    } finally {
      setLoadingWorkload(false);
    }
  };

  const handleSelect = (u: UserOption) => {
    setShowDropdown(false);
    setQuery(u.name);
    loadWorkload(u.id, u.name);
  };

  const handleSave = async (u: UserOption) => {
    try {
      const resp = await api.post('/people-workload/saved', {
        notion_user_id: u.id,
        name: u.name,
        avatar_url: u.avatar_url,
      });
      setSaved((prev) => {
        const existing = prev.findIndex((p) => p.notion_user_id === u.id);
        const row = resp.data as SavedPerson;
        if (existing >= 0) {
          const next = [...prev];
          next[existing] = row;
          return next;
        }
        return [...prev, row].sort((a, b) => a.name.localeCompare(b.name));
      });
    } catch (err: any) {
      alert('Save failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleUnsave = async (notion_user_id: string) => {
    try {
      await api.delete(`/people-workload/saved/${notion_user_id}`);
      setSaved((prev) => prev.filter((p) => p.notion_user_id !== notion_user_id));
    } catch (err: any) {
      alert('Remove failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // ----- Render -----
  const counts = workload?.by_status || {};
  const priorityCounts = workload?.by_priority || {};
  const orderedStatusKeys = [
    ...STATUS_ORDER.filter((k) => k in counts),
    ...Object.keys(counts).filter((k) => !STATUS_ORDER.includes(k)),
  ];
  const orderedPriorityKeys = [
    ...PRIORITY_ORDER.filter((k) => k in priorityCounts),
    ...Object.keys(priorityCounts).filter((k) => !PRIORITY_ORDER.includes(k)),
  ];

  return (
    <div className="max-w-6xl">
      <h2 className="text-lg font-semibold text-gray-900 mb-1">People Workload</h2>
      <p className="text-sm text-gray-600 mb-5">
        Search a Notion user to see their active tasks (excluding Done / Cancelled / Template).
        Save people you check often for quick access.
      </p>

      {/* Search + saved chips */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
        <div ref={searchRef} className="relative">
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowDropdown(true); }}
            onFocus={() => setShowDropdown(true)}
            placeholder={loadingUsers ? 'Loading users…' : `Search ${users.length} workspace users…`}
            disabled={loadingUsers}
            className="w-full border border-gray-300 rounded-md px-3 py-2 pr-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          {query && (
            <button
              type="button"
              onClick={() => { setQuery(''); setShowDropdown(true); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700"
            >
              ×
            </button>
          )}
          {showDropdown && (
            <div className="absolute z-20 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
              {filtered.length === 0 ? (
                <div className="px-3 py-2 text-sm text-gray-500">No users match.</div>
              ) : (
                filtered.map((u) => (
                  <div
                    key={u.id}
                    className="flex items-center justify-between gap-2 px-3 py-2 hover:bg-blue-50"
                  >
                    <button
                      type="button"
                      onClick={() => handleSelect(u)}
                      className="flex-1 text-left text-sm text-gray-800 min-w-0"
                    >
                      <div className="font-medium truncate">{u.name || '(unnamed)'}</div>
                      {u.email && <div className="text-xs text-gray-500 truncate">{u.email}</div>}
                    </button>
                    {isSaved(u.id) ? (
                      <span className="text-xs text-emerald-700 px-2 py-0.5 bg-emerald-50 border border-emerald-200 rounded">
                        Saved
                      </span>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleSave(u)}
                        className="text-xs px-2 py-0.5 text-blue-700 bg-white border border-blue-200 hover:bg-blue-50 rounded"
                      >
                        Save
                      </button>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {saved.length > 0 && (
          <div>
            <div className="text-xs text-gray-500 mb-1">Saved people</div>
            <div className="flex flex-wrap gap-2">
              {saved.map((p) => (
                <div
                  key={p.notion_user_id}
                  className={`flex items-center gap-1.5 pl-3 pr-1.5 py-1 text-sm rounded-full border ${
                    selectedUserId === p.notion_user_id
                      ? 'bg-blue-100 border-blue-300 text-blue-900'
                      : 'bg-white border-gray-300 text-gray-800 hover:bg-gray-50'
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => loadWorkload(p.notion_user_id, p.name)}
                    className="font-medium"
                  >
                    {p.name}
                  </button>
                  <button
                    type="button"
                    onClick={() => handleUnsave(p.notion_user_id)}
                    title="Remove from saved"
                    className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {error && (
        <div className="mt-4 p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md">
          {error}
        </div>
      )}

      {/* Workload view */}
      {selectedUserId && (
        <div className="mt-5 bg-white border border-gray-200 rounded-lg p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Total active</div>
              <div className="flex items-baseline gap-3">
                <span className="text-3xl font-semibold text-indigo-600">
                  {loadingWorkload ? '…' : workload?.total ?? 0}
                </span>
                <span className="text-base font-medium text-gray-800">{selectedName}</span>
              </div>
              {refreshedAt && (
                <div className="text-xs text-gray-400 mt-1">Last refreshed: {refreshedAt}</div>
              )}
            </div>
            <button
              onClick={() => loadWorkload(selectedUserId, selectedName)}
              disabled={loadingWorkload}
              className="px-3 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 rounded-md"
            >
              {loadingWorkload ? 'Refreshing…' : '↻ Refresh'}
            </button>
          </div>

          {/* Status breakdown */}
          <div className="mb-4">
            <div className="text-xs font-medium text-gray-700 mb-2">Tasks by status (click to list)</div>
            <div className="flex flex-wrap gap-2">
              {orderedStatusKeys.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() =>
                    setPopover((p) =>
                      p && p.kind === 'status' && p.key === k ? null : { kind: 'status', key: k }
                    )
                  }
                  className={`px-3 py-1.5 rounded-md border text-sm flex items-center gap-2 transition-shadow ${
                    STATUS_COLORS[k] || 'bg-gray-100 text-gray-700 border-gray-200'
                  } ${popover?.kind === 'status' && popover.key === k ? 'ring-2 ring-offset-1 ring-blue-400' : 'hover:shadow-sm'}`}
                >
                  <span>{k}</span>
                  <span className="font-semibold">{counts[k]}</span>
                </button>
              ))}
              {orderedStatusKeys.length === 0 && (
                <div className="text-sm text-gray-400">No active tasks.</div>
              )}
            </div>
          </div>

          {/* Priority breakdown */}
          <div className="mb-5">
            <div className="text-xs font-medium text-gray-700 mb-2">Tasks by priority (click to list)</div>
            <div className="flex flex-wrap gap-2">
              {orderedPriorityKeys.map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() =>
                    setPopover((p) =>
                      p && p.kind === 'priority' && p.key === k ? null : { kind: 'priority', key: k }
                    )
                  }
                  className={`px-3 py-1.5 rounded-md border text-sm flex items-center gap-2 transition-shadow ${
                    PRIORITY_COLORS[k] || 'bg-gray-100 text-gray-700 border-gray-200'
                  } ${popover?.kind === 'priority' && popover.key === k ? 'ring-2 ring-offset-1 ring-blue-400' : 'hover:shadow-sm'}`}
                >
                  <span>{k}</span>
                  <span className="font-semibold">{priorityCounts[k]}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Popover: filtered task list when a pill is clicked */}
          {popover && workload && (() => {
            const filtered = workload.tasks.filter((t) =>
              popover.kind === 'status' ? t.status === popover.key : t.priority === popover.key
            );
            return (
              <div className="mb-5 bg-white border border-gray-200 rounded-md shadow-sm">
                <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100">
                  <div className="text-sm font-semibold text-gray-900">
                    {popover.key} <span className="text-gray-500 font-normal">({filtered.length})</span>
                  </div>
                  <button
                    onClick={() => setPopover(null)}
                    className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700"
                    title="Close"
                  >
                    ×
                  </button>
                </div>
                <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                  {filtered.length === 0 ? (
                    <li className="px-3 py-3 text-sm text-gray-500">No tasks for this filter.</li>
                  ) : filtered.map((t) => (
                    <li key={t.id} className="px-3 py-2">
                      {t.url ? (
                        <a href={t.url} target="_blank" rel="noopener noreferrer"
                           className="text-sm text-blue-700 hover:underline font-medium">
                          {t.title}
                        </a>
                      ) : (
                        <div className="text-sm font-medium text-gray-900">{t.title}</div>
                      )}
                      <div className="text-xs text-gray-500 mt-0.5">
                        {(t.assignees && t.assignees[0]) || '—'} · {t.priority} · {t.status}
                        {t.due ? ` · Due ${formatDue(t.due)}` : ''}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {/* Calendar */}
          {workload && (
            <div className="mb-5">
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={() => {
                    const m = calMonth - 1;
                    if (m < 0) { setCalYear(calYear - 1); setCalMonth(11); }
                    else setCalMonth(m);
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900 px-2 py-1"
                >
                  ← Prev
                </button>
                <div className="text-sm font-semibold text-gray-900">{monthLabel(calYear, calMonth)}</div>
                <button
                  onClick={() => {
                    const m = calMonth + 1;
                    if (m > 11) { setCalYear(calYear + 1); setCalMonth(0); }
                    else setCalMonth(m);
                  }}
                  className="text-sm text-gray-600 hover:text-gray-900 px-2 py-1"
                >
                  Next →
                </button>
              </div>
              <div className="grid grid-cols-7 border border-gray-200 rounded-md overflow-hidden bg-white">
                {DAY_HEADERS.map((d) => (
                  <div key={d} className="bg-gray-50 px-2 py-1.5 text-xs font-medium text-gray-600 text-center border-b border-gray-200">
                    {d}
                  </div>
                ))}
                {(() => {
                  const firstDow = new Date(calYear, calMonth, 1).getDay();
                  const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
                  const cells: React.ReactNode[] = [];
                  // Leading blanks
                  for (let i = 0; i < firstDow; i++) {
                    cells.push(<div key={`b-${i}`} className="min-h-[92px] border-r border-b border-gray-100 bg-gray-50/40" />);
                  }
                  for (let d = 1; d <= daysInMonth; d++) {
                    const dayTasks = workload.tasks.filter((t) =>
                      t.due ? sameYMD(t.due, calYear, calMonth, d) : false
                    );
                    const isToday =
                      today.getFullYear() === calYear &&
                      today.getMonth() === calMonth &&
                      today.getDate() === d;
                    cells.push(
                      <div
                        key={`d-${d}`}
                        className={`min-h-[92px] p-1.5 border-r border-b border-gray-100 ${isToday ? 'bg-indigo-50/40 ring-1 ring-indigo-200' : ''}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs ${isToday ? 'text-indigo-700 font-semibold' : 'text-gray-500'}`}>{d}</span>
                          {dayTasks.length > 0 && (
                            <span className="text-[10px] text-gray-500 bg-gray-100 rounded-full px-1.5 py-px leading-none">
                              {dayTasks.length}
                            </span>
                          )}
                        </div>
                        <div className="space-y-1">
                          {dayTasks.slice(0, 3).map((t) => (
                            <a
                              key={t.id}
                              href={t.url || undefined}
                              target={t.url ? '_blank' : undefined}
                              rel="noopener noreferrer"
                              title={`${t.title}\n${t.assignees?.[0] || ''} · ${t.priority} · ${t.status}`}
                              className={`block px-1.5 py-1 rounded border text-[11px] leading-tight truncate ${
                                STATUS_COLORS[t.status] || 'bg-gray-50 text-gray-700 border-gray-200'
                              } hover:opacity-80`}
                            >
                              <div className="truncate font-medium">{t.title}</div>
                              <div className="truncate text-[10px] opacity-75">
                                {(t.assignees && t.assignees[0]) || ''}
                                {t.priority ? ` · ${t.priority}` : ''}
                              </div>
                            </a>
                          ))}
                          {dayTasks.length > 3 && (
                            <div className="text-[10px] text-gray-500 px-1.5">
                              +{dayTasks.length - 3} more
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }
                  // Trailing blanks to complete final row
                  const total = firstDow + daysInMonth;
                  const trailing = (7 - (total % 7)) % 7;
                  for (let i = 0; i < trailing; i++) {
                    cells.push(<div key={`t-${i}`} className="min-h-[92px] border-r border-b border-gray-100 bg-gray-50/40" />);
                  }
                  return cells;
                })()}
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                Tasks shown by their <strong>Due</strong> date. Click any card to open in Notion.
              </div>
            </div>
          )}

          {/* Active tasks table */}
          <div className="text-xs font-medium text-gray-700 mb-2">
            Active tasks {workload && `(${workload.total})`}
          </div>
          <div className="overflow-x-auto border border-gray-200 rounded-md">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">Task</th>
                  <th className="px-3 py-2 text-left font-medium">Status</th>
                  <th className="px-3 py-2 text-left font-medium">Priority</th>
                  <th className="px-3 py-2 text-left font-medium">Due</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {workload?.tasks?.length ? workload.tasks.map((t) => (
                  <tr key={t.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900 max-w-md">
                      {t.url ? (
                        <a href={t.url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                          {t.title}
                        </a>
                      ) : t.title}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_COLORS[t.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs border ${PRIORITY_COLORS[t.priority] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                        {t.priority}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">{formatDue(t.due)}</td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-sm text-gray-500">
                      {loadingWorkload ? 'Loading tasks…' : 'No active tasks for this person.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default PeopleWorkloadPanel;
